import { ExpandMore as ExpandIcon, Search } from "@mui/icons-material";
import {
  Box,
  FormHelperText,
  InputAdornment,
  List,
  Select,
  SelectProps,
} from "@mui/material";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { MuiSelectWrapper } from "../select/MuiSelect.style";
import { MuiTypography } from "../typography/MuiTypography";

import {
  getHelperText,
  isEmpty,
  isNotEmpty,
  removeWhiteSpace,
} from "../../helpers/utils";
import { MuiBox } from "../box/MuiBox";
import { MuiButtonPrimary } from "../button/MuiButton";
import MuiChipWithPopover from "../chipWithPopover/MuiChipWithPopover";
import { ErrorCrossIcon } from "../errorCrossIcon/ErrorCrossIcon";
import { MuiTextField } from "../textField/MuiTextfield";
import { MuiInfoToolTip } from "../tooltip/infoToolTip/MuiInfoToolTip";
import ListItemWithCheckbox from "./components/ListItemWithCheckbox";
import RenderValue from "./components/RenderValue";
import { getMergedInputProps } from "./helper";
import { StyledPopover } from "./MuiMultiSelect.style";
import { useMuiMultiSelectHook } from "./useMuiMultiSelectHook";

export interface MultiOptionsDataType {
  value: string | number;
  label: string;
  additionalInfo?: string;
  iconUrl?: string;
}

export interface MultiOptionARIAProps {
  requiredARIALabel: string;
  searchARIALabel: string;
  showMoreARIALabel: string;
  removeChipNameARIALabel: string;
  closePopoverARIALabel: string;
  checkedStateARIALabel: string;
  unCheckedStateARIALabel: string;
}

export type CustomMuiSelectProps = SelectProps & {
  label: string;
  options: MultiOptionsDataType[];
  showSearch?: boolean;
  className?: string;
  onSelectionChange?: (
    selectedIds: Array<string | number> | string | number,
  ) => void;
  isMandatory?: boolean;
  showChip: boolean; // Flag to control chip rendering
  showDropdown?: boolean; // Flag to control dropdown rendering
  textFieldWidth?: number;
  chipId?: string;
  dialogTitle?: string;
  selectedValues?: Array<string | number>;
  dialogWidth?: number;
  isChipEditable?: boolean;
  isChipExpandable?: boolean;
  helperText?: string;
  isErrorIconEnabled?: boolean;
  showSelectAllOption?: boolean;
  showDeclineAllOption?: boolean;
  onItemsChangedOrDropdownClosed?: (
    selectedValues: Array<string | number>,
  ) => void;
  onDropdownClicked?: () => void;
  reset?: boolean;
  isDialogTitleWithEllipsis?: boolean;
  visibleDoneButton?: boolean;
  selectPlaceHolder: string;
  searchPlaceHolder: string;
  selectAllPlaceHolder: string;
  declineAllPlaceHolder?: string;
  doneText: string;
  showDoubleLabel?: boolean; // Flag to control double label rendering
  chipDoubleLabelPattern?: string; // Pattern for double label in chips
  emptyMessage?: string;
  infoMessage?: boolean;
  tooltipMessage?: string;
  allARIAProps: MultiOptionARIAProps;
  onDeclineAll?: (declined: boolean) => void; // Callback for decline all action
  declinedValue?: boolean; // Flag to indicate if all options are declined
};

export const MuiMultiSelect = forwardRef((props: CustomMuiSelectProps, ref) => {
  const {
    selectRef,
    popoverWidth,
    anchorEl,
    searchText,
    selectedItem,
    selectedItems,
    selectedDataChip,
    itemRefs,
    focusedIndex,
    filteredOptions,
    handleOpen,
    handleClose,
    handleSearchChange,
    resetValues,
    handleSelectAll,
    handleDeclineAll,
    handleToggleItem,
    handleDelete,
    handleKeyDown,
    declinedAll,
  } = useMuiMultiSelectHook(props);

  const {
    id,
    label,
    options,
    helperText,
    className = "",
    disabled,
    showSearch = false,
    onSelectionChange,
    onItemsChangedOrDropdownClosed,
    isMandatory,
    showChip = false,
    showDropdown = true,
    textFieldWidth,
    chipId = id,
    dialogTitle = "",
    dialogWidth = 0,
    isChipEditable,
    isChipExpandable,
    showSelectAllOption = false,
    showDeclineAllOption = false,
    isErrorIconEnabled = true,
    reset,
    isDialogTitleWithEllipsis = false,
    visibleDoneButton = true,
    selectPlaceHolder,
    searchPlaceHolder,
    selectAllPlaceHolder,
    declineAllPlaceHolder,
    doneText,
    showDoubleLabel = false,
    chipDoubleLabelPattern = "",
    emptyMessage,
    infoMessage = false,
    tooltipMessage,
    allARIAProps,
    onDeclineAll,
    ...rest
  } = props;

  const {
    requiredARIALabel,
    searchARIALabel,
    closePopoverARIALabel,
    showMoreARIALabel,
    removeChipNameARIALabel,
    checkedStateARIALabel,
    unCheckedStateARIALabel,
  } = allARIAProps;

  useImperativeHandle(ref, () => ({
    resetValues,
  }));

  useEffect(() => {
    itemRefs.current[focusedIndex]?.focus();
  }, [focusedIndex]);

  return (
    <MuiSelectWrapper className={className}>
      {label && (
        <MuiTypography
          className={disabled ? "mui-label disabled" : "mui-label"}
          component="label"
          variant="h3"
        >
          {label} {isMandatory && <label className="asterisk">*</label>}
        </MuiTypography>
      )}
      {showDropdown && (
        <>
          <Select
            inputProps={getMergedInputProps(props)}
            displayEmpty
            renderValue={() => (
              <RenderValue
                showDoubleLabel={showDoubleLabel}
                options={options}
                selectedItem={selectedItem}
                selectPlaceHolder={selectPlaceHolder}
              />
            )}
            IconComponent={ExpandIcon}
            ref={selectRef}
            onClick={disabled ? undefined : handleOpen}
            {...rest}
            // Only mark Select as error when popover is closed
            error={Boolean(rest.error && !anchorEl)}
            disabled={disabled}
            className="mui-select"
            open={false} // Disable built-in dropdown to use custom popover
            value="" // Mandatory to avoid warning - in case of multi-select
            onBlur={(event) => {
              // Prevent blur if popover is open
              if (anchorEl) {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
          />

          <StyledPopover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            popoverWidth={popoverWidth}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
          >
            <MuiBox>
              {showSearch && (
                <MuiTextField
                  placeholder={searchPlaceHolder}
                  value={searchText}
                  onChange={handleSearchChange}
                  showCloseIcon
                  startAdornment={
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  }
                  aria-label={searchARIALabel}
                  requiredARIALabel={requiredARIALabel}
                />
              )}
              <List className="options-list" onKeyDown={handleKeyDown}>
                {(() => {
                  const hasFilteredOptions = filteredOptions.length > 0;
                  if (hasFilteredOptions) {
                    return (
                      <ListItemWithCheckbox
                        selectedItems={selectedItems}
                        focusedIndex={focusedIndex}
                        disabled={disabled}
                        showDoubleLabel={showDoubleLabel}
                        handleToggleItem={handleToggleItem}
                        itemRefs={itemRefs}
                        showSelectAllOption={showSelectAllOption}
                        showDeclineAllOption={showDeclineAllOption}
                        searchText={searchText}
                        filteredOptions={filteredOptions}
                        selectAllPlaceHolder={selectAllPlaceHolder}
                        declineAllPlaceHolder={declineAllPlaceHolder}
                        handleSelectAll={handleSelectAll}
                        handleDeclineAll={() => {
                          handleDeclineAll?.();
                          if (onDeclineAll) {
                            onDeclineAll(!declinedAll);
                          }
                        }}
                        declinedAll={declinedAll}
                        checkedStateARIALabel={checkedStateARIALabel}
                        unCheckedStateARIALabel={unCheckedStateARIALabel}
                      />
                    );
                  } else if (isEmpty(options) && isNotEmpty(emptyMessage)) {
                    return (
                      <MuiBox className="empty-message">
                        <MuiTypography className="h3">
                          {emptyMessage}
                        </MuiTypography>
                      </MuiBox>
                    );
                  } else {
                    return null;
                  }
                })()}
              </List>
              {visibleDoneButton && filteredOptions.length > 0 && (
                <MuiBox className="done-button-container">
                  <MuiButtonPrimary
                    onClick={handleClose}
                    id="done-button"
                    data-testid="done-button"
                  >
                    {doneText}
                  </MuiButtonPrimary>
                </MuiBox>
              )}
            </MuiBox>
          </StyledPopover>
        </>
      )}

      {showChip && selectedDataChip.length > 0 && (
        <Box className="mui-chip-wrapper">
          <MuiChipWithPopover
            data={selectedDataChip}
            handleDelete={handleDelete}
            id={chipId}
            isEditable={isChipEditable}
            isExpanded={isChipExpandable}
            headerLabel={dialogTitle}
            gridRef={selectRef}
            isDialogTitleWithEllipsis={isDialogTitleWithEllipsis}
            showMoreARIALabel={showMoreARIALabel}
            removeChipNameARIALabel={removeChipNameARIALabel}
            closePopoverARIALabel={closePopoverARIALabel}
            showDoubleLabel={showDoubleLabel}
            chipDoubleLabelPattern={chipDoubleLabelPattern}
          />
        </Box>
      )}
      {rest.error && !anchorEl && (
        <FormHelperText error={rest.error}>
          {helperText && (
            <MuiBox
              role="alert"
              aria-description={getHelperText(helperText, label)}
              className="error-icon"
            >
              {isErrorIconEnabled && <ErrorCrossIcon />}
              <div aria-hidden id={`helper-text-${removeWhiteSpace(label)}`}>
                {getHelperText(helperText, label)}
              </div>

              {infoMessage && (
                <MuiInfoToolTip
                  placement="bottom-end"
                  message={
                    <span style={{ whiteSpace: "pre-line" }}>
                      {tooltipMessage}
                    </span>
                  }
                />
              )}
            </MuiBox>
          )}
        </FormHelperText>
      )}
    </MuiSelectWrapper>
  );
});
